import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { buildSignupExtras, classifySignupEvent, currentSignupConfig, loadSignupConfig } from "./metaEsVersion";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import {
	Dialog,
	DialogContent,
	DialogTitle,
	Button,
	DialogActions,
	CircularProgress,
	TextField,
	Switch,
	FormControlLabel,
	MenuItem,
	Typography,
	Chip,
	Box,
	Radio,
	RadioGroup,
	Paper,
	InputAdornment,
	IconButton,
	Tooltip,
} from "@material-ui/core";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},

	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},

	sectionTitle: {
		marginTop: theme.spacing(1.5),
		marginBottom: theme.spacing(0.5),
		color: theme.palette.text.secondary,
		fontSize: "0.72rem",
		fontWeight: 700,
		textTransform: "uppercase",
		letterSpacing: "0.08em",
	},

	typeCard: {
		padding: theme.spacing(1.5, 2),
		marginBottom: theme.spacing(1),
		borderRadius: 8,
		cursor: "pointer",
		border: "2px solid transparent",
		transition: "border-color 0.15s, background 0.15s",
		display: "flex",
		alignItems: "flex-start",
		gap: theme.spacing(1),
	},

	typeCardSelected: {
		borderColor: theme.palette.primary.main,
		backgroundColor: theme.palette.primary.main + "10",
	},

	typeCardDisabled: {
		opacity: 0.45,
		cursor: "not-allowed",
	},
}));

const TYPE_MAP = {
	whaileys: {
		labelKey: "connections.types.qrPro",
		descKey: "connections.types.qrProDesc",
		color: "#10b981",
	},
	wwebjs: {
		labelKey: "connections.types.qrWeb",
		descKey: "connections.types.qrWebDesc",
		color: "#3b82f6",
	},
	meta: {
		labelKey: "connections.types.metaCloud",
		descKey: "connections.types.metaCloudDesc",
		color: "#7c3aed",
	},
	meta_coexistence: {
		labelKey: "connections.types.coexistence",
		descKey: "connections.types.coexistenceDesc",
		color: "#f97316",
	},
};

const getTypeInfo = (provider) => {
	const t = TYPE_MAP[provider];
	if (!t) return { label: provider, desc: "", color: "#6b7280" };
	return {
		label: i18n.t(t.labelKey),
		desc: i18n.t(t.descKey),
		color: t.color,
	};
};

const SessionSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
});

const WhatsAppModal = ({ open, onClose, whatsAppId }) => {
	const classes = useStyles();

	// Step 1 = type selection (create only), Step 2 = configure
	const [step, setStep] = useState(1);
	const [selectedType, setSelectedType] = useState("whaileys");
	const [allowedTypes, setAllowedTypes] = useState(["whaileys"]);
	const [showMetaToken, setShowMetaToken] = useState(false);
	const [updateTokenMode, setUpdateTokenMode] = useState(false);
	const [hasExistingMetaToken, setHasExistingMetaToken] = useState(false);
	const [embeddedSignupLoading, setEmbeddedSignupLoading] = useState(false);
	const [metaInputMode, setMetaInputMode] = useState("signup"); // "signup" | "manual"
	// Holds { phoneNumberId, wabaId, businessId } captured from the WA_EMBEDDED_SIGNUP
	// message event so handleFbResponse can send them with the code.
	const embeddedSignupDataRef = useRef(null);
	const embeddedSignupListenerRef = useRef(null);

	const initialState = {
		name: "",
		greetingMessage: "",
		farewellMessage: "",
		isDefault: false,
		linkedWhatsappId: "",
		isPrimary: true,
		metaPhoneNumberId: "",
		metaWabaId: "",
		metaAccessToken: "",
		metaAppId: "",
		n8nWebhookUrl: "",
	};
	const [whatsApp, setWhatsApp] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [allConnections, setAllConnections] = useState([]);

	// Load Facebook SDK when modal opens for Meta type
	useEffect(() => {
		const isMeta = selectedType === "meta" || selectedType === "meta_coexistence";
		if (!open || !isMeta) return;
		// Fase 168 — la versión del alta (v4 / v2 de rollback) se lee al abrir, nunca en el clic.
		loadSignupConfig();
		if (window.FB) return;
		const script = document.createElement("script");
		script.src = "https://connect.facebook.net/es_LA/sdk.js";
		script.async = true;
		script.defer = true;
		document.body.appendChild(script);
	}, [open, selectedType]);

	// Fetch allowed types from settings
	useEffect(() => {
		if (!open) return;
		api.get("/settings").then(({ data }) => {
			const s = Array.isArray(data) ? data.find(x => x.key === "allowedConnectionTypes") : null;
			if (s) {
				try { setAllowedTypes(JSON.parse(s.value)); } catch (_) {}
			}
		}).catch(() => {});
	}, [open]);

	// Keep the selected type within the enabled set (create mode). If the default
	// (whaileys) is not enabled for this company, fall back to the first enabled.
	useEffect(() => {
		if (!open || whatsAppId) return;
		if (allowedTypes.length && !allowedTypes.includes(selectedType)) {
			setSelectedType(allowedTypes[0]);
		}
	}, [allowedTypes, open, whatsAppId, selectedType]);

	// Reset on open
	useEffect(() => {
		if (!open) return;
		if (!whatsAppId) {
			setStep(1);
			setSelectedType("whaileys");
			setMetaInputMode("signup");
			setWhatsApp(initialState);
			setSelectedQueueIds([]);
			setAllConnections([]);
			return;
		}
		// Edit mode
		setStep(2);
		const fetchSession = async () => {
			try {
				const { data } = await api.get(`whatsapp/${whatsAppId}`);
				setWhatsApp({
					...initialState,
					...data,
					linkedWhatsappId: data.linkedWhatsappId ?? "",
					isPrimary: data.isPrimary !== false,
					// Meta fields — null in DB becomes "" for inputs
					metaPhoneNumberId: data.metaPhoneNumberId || "",
					metaWabaId: data.metaWabaId || "",
					metaAppId: data.metaAppId || "",
					metaAccessToken: "", // never pre-populate token
				});
				setSelectedQueueIds(data.queues?.map(q => q.id) || []);
				setSelectedType(data.provider || "whaileys");
				// Track whether a token already exists (encrypted value in DB)
				setHasExistingMetaToken(!!data.metaAccessToken);
				setUpdateTokenMode(!data.metaAccessToken); // auto-open if no token yet
			} catch (err) {
				toastError(err);
			}
		};
		const fetchAll = async () => {
			try {
				const { data } = await api.get("/whatsapp");
				setAllConnections(Array.isArray(data) ? data : []);
			} catch (_) {}
		};
		fetchSession();
		fetchAll();
	}, [open, whatsAppId]);

	// Remove any lingering Embedded Signup message listener on unmount.
	useEffect(() => {
		return () => {
			if (embeddedSignupListenerRef.current) {
				window.removeEventListener("message", embeddedSignupListenerRef.current);
				embeddedSignupListenerRef.current = null;
			}
		};
	}, []);

	const handleEmbeddedSignup = (connectionName) => {
		const isCoexistence = selectedType === "meta_coexistence";
		// Coexistence and Cloud API are two distinct Meta Embedded Signup flows:
		// different config_id + login feature. provider (= selectedType) already tells
		// the backend which one was used.
		// Fase 168 — versión (v4 / v2) e id de config v4 nuevo desde /meta-es.json; si no, los de siempre.
		const es = currentSignupConfig();
		const configId = isCoexistence
			? (es.configIdCoex || (window.ENV && window.ENV.VITE_META_COEXISTENCE_CONFIG_ID) || process.env.REACT_APP_META_COEXISTENCE_CONFIG_ID || "27182954768039004")
			: (es.configIdCloud || (window.ENV && window.ENV.VITE_META_EMBEDDED_SIGNUP_CONFIG_ID) || process.env.REACT_APP_META_EMBEDDED_SIGNUP_CONFIG_ID);
		const appId = (window.ENV && window.ENV.VITE_META_APP_ID) || process.env.REACT_APP_META_APP_ID;

		if (!window.FB) {
			toast.error("Facebook SDK not loaded");
			return;
		}

		window.FB.init({
			appId: appId || "1488010632180493",
			autoLogAppEvents: true,
			xfbml: true,
			version: "v19.0"
		});

		// Capture the WA_EMBEDDED_SIGNUP message event. Meta posts it to window with
		// the ids of the just-onboarded number. Two flows emit it:
		//  · Cloud API (whatsapp_embedded_signup): events FINISH / FINISH_ONLY_WABA →
		//    payload trae phone_number_id + waba_id.
		//  · Coexistence (whatsapp_business_app_onboarding): event
		//    FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING → payload trae SOLO waba_id (no
		//    phone_number_id). El backend descubre el phone con GET /{waba_id}/phone_numbers.
		// Necesitamos estos ids porque el token de Embedded Signup no sirve para la
		// discovery por /me/businesses (no tiene scope business_management).
		embeddedSignupDataRef.current = null;
		const messageHandler = (event) => {
			if (!event.origin || !/facebook\.com$/.test(new URL(event.origin).hostname)) return;
			let data;
			try {
				data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
			} catch (e) {
				return; // not an embedded signup message
			}
			// Fase 168 — mismos nombres en v2 y v4; en v4 el error reportado llega como CANCEL + error_message.
			const kind = classifySignupEvent(data);
			if (kind === "ignore") return;
			if (kind === "finish") {
				embeddedSignupDataRef.current = {
					// Coexistence trae solo waba_id; phone_number_id queda undefined y el
					// backend lo resuelve desde la WABA.
					phoneNumberId: data.data?.phone_number_id,
					wabaId: data.data?.waba_id,
					businessId: data.data?.business_id
				};
			} else if (kind === "cancel") {
				toast.error(i18n.t("embeddedSignup.cancelled"));
			} else if (kind === "error") {
				toast.error(i18n.t("embeddedSignup.error") + (data.data?.error_message ? `: ${data.data.error_message}` : ""));
			}
		};
		// Drop any stale listener from a previous click before adding a fresh one.
		if (embeddedSignupListenerRef.current) {
			window.removeEventListener("message", embeddedSignupListenerRef.current);
		}
		embeddedSignupListenerRef.current = messageHandler;
		window.addEventListener("message", messageHandler);

		const cleanupListener = () => {
			if (embeddedSignupListenerRef.current) {
				window.removeEventListener("message", embeddedSignupListenerRef.current);
				embeddedSignupListenerRef.current = null;
			}
		};

		// FB.login does NOT accept an async callback (it rejects with
		// "Expression is of type asyncfunction, not function" and never opens the
		// popup). Callback stays a plain function; the async POST runs separately.
		const handleFbResponse = async (response) => {
			if (!response?.authResponse?.code) {
				cleanupListener();
				return;
			}
			const code = response.authResponse.code;
			const signupData = embeddedSignupDataRef.current || {};
			setEmbeddedSignupLoading(true);
			try {
				await api.post("/whatsapp/meta-embedded-signup", {
					code,
					provider: selectedType,
					connectionName: connectionName || "",
					// Sent from the WA_EMBEDDED_SIGNUP event; backend uses them to skip
					// the Business Management discovery that 400s for these tokens.
					phoneNumberId: signupData.phoneNumberId,
					wabaId: signupData.wabaId
				});
				toast.success(i18n.t("embeddedSignup.success"));
				handleClose();
			} catch (err) {
				const errKey = err.response?.data?.message;
				toast.error(i18n.t("embeddedSignup.error") + (errKey ? `: ${errKey}` : ""));
			}
			setEmbeddedSignupLoading(false);
			cleanupListener();
		};

		window.FB.login((response) => {
			handleFbResponse(response);
		}, {
			config_id: configId || "",
			response_type: "code",
			override_default_response_type: true,
			extras: buildSignupExtras(selectedType, es.version)
		});
	};

	const handleSaveWhatsApp = async values => {
		const linkedId = values.linkedWhatsappId ? Number(values.linkedWhatsappId) : null;
		const provider = whatsAppId ? (whatsApp.provider || selectedType) : selectedType;
		const isMeta = provider === "meta" || provider === "meta_coexistence";

		// Guard: never create an EMPTY Meta connection. The Embedded Signup path
		// completes via the FB popup (handleEmbeddedSignup), not via this submit.
		// If someone hits "Add" while on the signup sub-mode (manual fields hidden),
		// we'd otherwise persist a connection with no phone/token (the "Waia2" bug).
		if (!whatsAppId && isMeta && (!values.metaPhoneNumberId || !values.metaAccessToken)) {
			toast.error(i18n.t("embeddedSignup.emptyMetaError"));
			return;
		}

		const payload = {
			...values,
			provider,
			queueIds: selectedQueueIds,
			// feat_205: el failover (conexión de respaldo) es SOLO-QR. En Meta no aplica
			// (un número Meta se conecta una sola vez a Cloud API) → forzar null/primary.
			linkedWhatsappId: isMeta ? null : linkedId,
			isPrimary: isMeta ? true : (values.isPrimary !== false),
			// Only include meta token if it's a new value (not the hidden placeholder)
			...(isMeta && values.metaPhoneNumberId && { metaPhoneNumberId: values.metaPhoneNumberId }),
			...(isMeta && values.metaWabaId && { metaWabaId: values.metaWabaId }),
			...(isMeta && values.metaAppId && { metaAppId: values.metaAppId }),
			...(isMeta && values.metaAccessToken && updateTokenMode && { metaAccessToken: values.metaAccessToken }),
			...(isMeta && !whatsAppId && values.metaAccessToken && { metaAccessToken: values.metaAccessToken }),
		};
		try {
			if (whatsAppId) {
				await api.put(`/whatsapp/${whatsAppId}`, payload);
			} else {
				await api.post("/whatsapp", payload);
			}
			toast.success(i18n.t("whatsappModal.success"));
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	const handleUnlink = async () => {
		try {
			await api.put(`/whatsapp/${whatsAppId}`, {
				linkedWhatsappId: null,
				isPrimary: true,
			});
			toast.success(i18n.t("whatsappModal.success"));
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	const handleClose = () => {
		onClose();
		setWhatsApp(initialState);
		setAllConnections([]);
		setStep(1);
		setUpdateTokenMode(false);
		setHasExistingMetaToken(false);
		setShowMetaToken(false);
	};

	// Connections available as backup (same number, or all if no number)
	const thisNum = whatsApp.number || whatsApp.phoneNumber || "";
	const availableForBackup = allConnections.filter(w => {
		if (w.id === Number(whatsAppId)) return false;
		// Already linked to this connection → always show
		if (w.linkedWhatsappId === Number(whatsAppId)) return true;
		// Already linked to someone else → exclude
		if (w.linkedWhatsappId && w.linkedWhatsappId !== Number(whatsAppId)) return false;
		// TODO: if superadmin enabled cross-number, return all here
		if (!thisNum) return true; // No number on current → show all
		const otherNum = w.number || w.phoneNumber || "";
		if (!otherNum) return false;
		return thisNum === otherNum;
	});

	// Find primary name for standby display
	const primaryConnection = allConnections.find(w => w.id === Number(whatsApp.linkedWhatsappId));

	// All supported type keys — ordered by priority
	const allTypeKeys = ["whaileys", "wwebjs", "meta", "meta_coexistence"];
	// Only types enabled for this company (allowedConnectionTypes) are shown.
	// Types NOT enabled are not rendered at all (feat_131).
	const visibleTypeKeys = allTypeKeys.filter(typeKey => allowedTypes.includes(typeKey));

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="sm"
				fullWidth
				scroll="paper"
			>
				<DialogTitle>
					{whatsAppId
						? i18n.t("whatsappModal.title.edit")
						: step === 1
							? i18n.t("connections.stepSelectType")
							: i18n.t("connections.stepConfigure")}
				</DialogTitle>

				{/* ── STEP 1: Type selection (create only) ── */}
				{!whatsAppId && step === 1 && (
					<>
						<DialogContent dividers>
							{visibleTypeKeys.length === 0 ? (
								<Typography variant="body2" color="textSecondary" style={{ padding: 8 }}>
									{i18n.t("connections.noTypesEnabled")}
								</Typography>
							) : (
								<RadioGroup
									value={selectedType}
									onChange={e => setSelectedType(e.target.value)}
								>
									{visibleTypeKeys.map(typeKey => {
										const info = getTypeInfo(typeKey);
										return (
											<Paper
												key={typeKey}
												elevation={0}
												variant="outlined"
												className={`${classes.typeCard} ${selectedType === typeKey ? classes.typeCardSelected : ""}`}
												onClick={() => setSelectedType(typeKey)}
											>
												<Radio
													value={typeKey}
													size="small"
													style={{ padding: 2, color: info.color }}
												/>
												<div>
													<Typography variant="body2" style={{ fontWeight: 700, color: info.color }}>
														{info.label}
													</Typography>
													<Typography variant="caption">
														{info.desc}
													</Typography>
												</div>
											</Paper>
										);
									})}
								</RadioGroup>
							)}
						</DialogContent>
						<DialogActions>
							<Button onClick={handleClose} color="secondary" variant="outlined">
								{i18n.t("whatsappModal.buttons.cancel")}
							</Button>
							<Button
								color="primary"
								variant="contained"
								onClick={() => setStep(2)}
								disabled={visibleTypeKeys.length === 0}
							>
								{i18n.t("whatsappModal.buttons.next")}
							</Button>
						</DialogActions>
					</>
				)}

				{/* ── STEP 2: Configure ── */}
				{step === 2 && (
					<Formik
						initialValues={whatsApp}
						enableReinitialize={true}
						validationSchema={SessionSchema}
						onSubmit={(values, actions) => {
							setTimeout(() => {
								handleSaveWhatsApp(values);
								actions.setSubmitting(false);
							}, 400);
						}}
					>
						{({ values, touched, errors, isSubmitting, setFieldValue }) => (
							<Form>
								<DialogContent dividers>
									{/* Type chip (edit mode) */}
									{whatsAppId && (
										<Box mb={1.5} display="flex" alignItems="center" gap={1}>
											{(() => {
												const info = getTypeInfo(whatsApp.provider || selectedType);
												return (
													<Chip
														label={info.label}
														size="small"
														style={{ backgroundColor: info.color + "20", color: info.color, fontWeight: 700, border: `1px solid ${info.color}` }}
													/>
												);
											})()}
											<Typography variant="caption" color="textSecondary">
												{i18n.t("whatsappModal.title.edit")}
											</Typography>
										</Box>
									)}

									<div className={classes.multFieldLine}>
										<Field
											as={TextField}
											label={i18n.t("whatsappModal.form.name")}
											autoFocus
											name="name"
											error={touched.name && Boolean(errors.name)}
											helperText={touched.name && errors.name}
											variant="outlined"
											margin="dense"
											style={{ flex: 1 }}
										/>
										<FormControlLabel
											control={
												<Field
													as={Switch}
													color="primary"
													name="isDefault"
													checked={values.isDefault}
												/>
											}
											label={i18n.t("whatsappModal.form.default")}
										/>
									</div>

									{/* Meta API fields — only for meta/meta_coexistence */}
									{(selectedType === "meta" || selectedType === "meta_coexistence" ||
									  whatsApp.provider === "meta" || whatsApp.provider === "meta_coexistence") && (
										<Box mt={1} mb={1} p={1.5} style={{ background: "#f5f3ff", borderRadius: 8, border: "1px solid #ddd6fe" }}>
											<Typography className={classes.sectionTitle} style={{ color: "#7c3aed" }}>
												Meta API — WhatsApp Cloud
											</Typography>
											{/* Embedded Signup — only in create mode */}
											{!whatsAppId && (
												<Box mb={1.5}>
													{metaInputMode === "signup" ? (
														<Box display="flex" flexDirection="column" gap={8} alignItems="center">
															<Button
																variant="contained"
																fullWidth
																onClick={() => handleEmbeddedSignup(values.name)}
																disabled={embeddedSignupLoading}
																style={{ background: "#1877f2", color: "#fff", textTransform: "none", fontWeight: 600 }}
																startIcon={embeddedSignupLoading ? <CircularProgress size={16} style={{ color: "#fff" }} /> : null}
															>
																{embeddedSignupLoading ? i18n.t("embeddedSignup.connecting") : i18n.t("embeddedSignup.connectButton")}
															</Button>
															<Button
																size="small"
																variant="text"
																color="default"
																onClick={() => setMetaInputMode("manual")}
																style={{ marginTop: 4, fontSize: 11 }}
															>
																{i18n.t("embeddedSignup.manualButton")}
															</Button>
														</Box>
													) : (
														<Button
															size="small"
															variant="text"
															color="primary"
															onClick={() => setMetaInputMode("signup")}
															style={{ fontSize: 11 }}
														>
															← {i18n.t("embeddedSignup.connectButton")}
														</Button>
													)}
												</Box>
											)}
											{/* Manual fields — always shown in edit mode, in create mode only if "manual" selected */}
											{(whatsAppId || metaInputMode === "manual") && (
											<>
											<Field
												as={TextField}
												label={i18n.t("whatsappModal.meta.phoneNumberId")}
												name="metaPhoneNumberId"
												fullWidth
												variant="outlined"
												margin="dense"
												InputProps={{
													endAdornment: (
														<InputAdornment position="end">
															<Tooltip title={i18n.t("whatsappModal.meta.whereToFind")}>
																<HelpOutlineIcon fontSize="small" style={{ color: "#9ca3af", cursor: "help" }} />
															</Tooltip>
														</InputAdornment>
													)
												}}
											/>
											<Field
												as={TextField}
												label={i18n.t("whatsappModal.meta.wabaId")}
												name="metaWabaId"
												fullWidth
												variant="outlined"
												margin="dense"
											/>
											<Field
												as={TextField}
												label={i18n.t("whatsappModal.meta.appId")}
												name="metaAppId"
												fullWidth
												variant="outlined"
												margin="dense"
											/>
											{/* Access token — hide if already set in edit mode */}
											{whatsAppId && hasExistingMetaToken && !updateTokenMode ? (
												<Box display="flex" alignItems="center" mt={0.5} gap={1}>
													<Typography variant="caption" style={{ color: "#6b7280", fontFamily: "monospace" }}>
														{i18n.t("whatsappModal.meta.tokenHidden")}
													</Typography>
													<Button
														size="small"
														variant="text"
														color="primary"
														onClick={() => setUpdateTokenMode(true)}
														style={{ fontSize: 11 }}
													>
														{i18n.t("whatsappModal.meta.updateToken")}
													</Button>
												</Box>
											) : (
												<Field
													as={TextField}
													label={i18n.t("whatsappModal.meta.accessToken")}
													name="metaAccessToken"
													fullWidth
													variant="outlined"
													margin="dense"
													type={showMetaToken ? "text" : "password"}
													InputProps={{
														endAdornment: (
															<InputAdornment position="end">
																<IconButton size="small" onClick={() => setShowMetaToken(v => !v)}>
																	{showMetaToken ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
																</IconButton>
															</InputAdornment>
														)
													}}
												/>
											)}
											</>
											)}
										</Box>
									)}

									<QueueSelect
										selectedQueueIds={selectedQueueIds}
										onChange={ids => setSelectedQueueIds(ids)}
									/>

									<div>
										<Field
											as={TextField}
											label={i18n.t("queueModal.form.greetingMessage")}
											multiline
											rows={4}
											fullWidth
											name="greetingMessage"
											variant="outlined"
											margin="dense"
										/>
									</div>
									<div>
										<Field
											as={TextField}
											label={i18n.t("whatsappModal.form.farewellMessage")}
											multiline
											rows={4}
											fullWidth
											name="farewellMessage"
											variant="outlined"
											margin="dense"
										/>
									</div>
									<div>
										<Field
											as={TextField}
											label={i18n.t("whatsappModal.form.n8nWebhookUrl")}
											helperText={i18n.t("whatsappModal.form.n8nWebhookUrlHelp")}
											fullWidth
											name="n8nWebhookUrl"
											variant="outlined"
											margin="dense"
											placeholder="https://n8n.example.com/webhook/..."
										/>
									</div>

									{/* Backup section — edit mode only, y SOLO para QR (feat_205).
									    En Meta no hay failover (Cloud API, una sola sesión por número). */}
									{whatsAppId && !(selectedType === "meta" || selectedType === "meta_coexistence" ||
									  whatsApp.provider === "meta" || whatsApp.provider === "meta_coexistence") && (
										<Box mt={2}>
											<Typography className={classes.sectionTitle}>
												{i18n.t("whatsappModal.form.backupConnection")}
											</Typography>

											{values.isPrimary === false ? (
												/* This IS a standby */
												<Box
													display="flex"
													alignItems="center"
													style={{
														padding: "8px 12px",
														background: "#f3f4f6",
														borderRadius: 6,
														gap: 8,
													}}
												>
													<Typography variant="body2">
														{i18n.t("whatsappModal.form.backupOf")}{" "}
														<strong>{primaryConnection?.name || "—"}</strong>
													</Typography>
													<Button
														size="small"
														variant="outlined"
														color="secondary"
														onClick={handleUnlink}
														style={{ marginLeft: "auto" }}
													>
														{i18n.t("whatsappModal.form.unlinkBackup")}
													</Button>
												</Box>
											) : (
												/* This IS a primary — show dropdown */
												<Field
													as={TextField}
													select
													name="linkedWhatsappId"
													value={values.linkedWhatsappId ?? ""}
													onChange={e => setFieldValue("linkedWhatsappId", e.target.value)}
													variant="outlined"
													margin="dense"
													fullWidth
													label={i18n.t("whatsappModal.form.backupConnection")}
												>
													<MenuItem value="">
														{i18n.t("whatsappModal.form.noBackup")}
													</MenuItem>
													{availableForBackup.map(w => {
														const statusDot = w.status === "CONNECTED" ? "🟢" : "🔴";
														return (
															<MenuItem key={w.id} value={w.id}>
																{statusDot} {w.name}
																{w.number || w.phoneNumber
																	? ` (${w.number || w.phoneNumber})`
																	: ""}
															</MenuItem>
														);
													})}
												</Field>
											)}
										</Box>
									)}
								</DialogContent>

								<DialogActions>
									{!whatsAppId && (
										<Button onClick={() => setStep(1)} color="default" variant="text">
											← Atrás
										</Button>
									)}
									<Button
										onClick={handleClose}
										color="secondary"
										disabled={isSubmitting}
										variant="outlined"
									>
										{i18n.t("whatsappModal.buttons.cancel")}
									</Button>
									{/* In create mode, while on the Meta Embedded Signup sub-mode the
									    connection is created by the FB popup, not by this submit — hide
									    "Add" so nobody persists an empty Meta connection. */}
									{!(!whatsAppId &&
									   (selectedType === "meta" || selectedType === "meta_coexistence") &&
									   metaInputMode === "signup") && (
										<Button
											type="submit"
											color="primary"
											disabled={isSubmitting}
											variant="contained"
											className={classes.btnWrapper}
										>
											{whatsAppId
												? i18n.t("whatsappModal.buttons.okEdit")
												: i18n.t("whatsappModal.buttons.okAdd")}
											{isSubmitting && (
												<CircularProgress
													size={24}
													className={classes.buttonProgress}
												/>
											)}
										</Button>
									)}
								</DialogActions>
							</Form>
						)}
					</Formik>
				)}
			</Dialog>
		</div>
	);
};

export default React.memo(WhatsAppModal);
